/**
 * AccessibilityManager — manages ARIA attributes, focus indicators, and keyboard navigation.
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4, 12.1, 12.2, 12.3, 12.4, 12.5
 */

import type { TranslationKey } from "../i18n/TranslationManager";

export interface A11yConfig {
  elementType: string;
  required?: boolean;
  label?: string;
  translate?: (key: TranslationKey) => string;
}

export type EscapeCallback = (elementType: string) => void;

export const AccessibilityManager = {
  /**
   * Set ARIA attributes on an input element.
   * When a translate function is provided, resolves ARIA labels via translate(ariaKey)
   * instead of hardcoded defaults.
   * Requirements: 11.1, 11.2, 11.3, 11.4, 12.5
   */
  applyAriaAttributes(input: HTMLInputElement, config: A11yConfig): void {
    input.setAttribute("role", "textbox");
    const label = config.translate
      ? config.translate(getAriaKey(config.elementType))
      : config.label ?? getDefaultLabel(config.elementType);
    input.setAttribute("aria-label", label);
    input.setAttribute("aria-invalid", "false");
    input.setAttribute("aria-required", config.required !== false ? "true" : "false");
  },

  /**
   * Update aria-invalid based on validation state.
   */
  setInvalid(input: HTMLInputElement, invalid: boolean): void {
    input.setAttribute("aria-invalid", String(invalid));
  },

  /**
   * Apply visible focus indicator styles that meet WCAG 2.1 AA contrast.
   * Requirements: 12.3
   */
  applyFocusStyles(container: HTMLElement): void {
    const existingStyle = container.querySelector("style[data-sf-a11y]");
    if (existingStyle) return;

    const styleEl = container.ownerDocument.createElement("style");
    styleEl.setAttribute("data-sf-a11y", "true");
    styleEl.textContent = `
      .sf-input:focus {
        outline: 2px solid var(--sf-color-primary, #0570de);
        outline-offset: 2px;
      }
      .sf-input:focus:not(:focus-visible) {
        outline: none;
      }
      .sf-input:focus-visible {
        outline: 2px solid var(--sf-color-primary, #0570de);
        outline-offset: 2px;
      }
    `;
    container.appendChild(styleEl);
  },

  /**
   * Set up keyboard event listeners for Tab/Shift+Tab navigation and Escape key.
   * Requirements: 12.1, 12.2, 12.4
   */
  setupKeyboardHandlers(
    input: HTMLInputElement,
    elementType: string,
    onEscape: EscapeCallback
  ): () => void {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onEscape(elementType);
      }
      // Tab and Shift+Tab are handled natively by the browser for iframe focus traversal
    };

    input.addEventListener("keydown", handler);

    // Return cleanup function
    return () => {
      input.removeEventListener("keydown", handler);
    };
  },

  /**
   * Make the input element tabbable (reachable via Tab key).
   * Requirements: 12.1
   */
  makeTabbable(input: HTMLInputElement): void {
    input.setAttribute("tabindex", "0");
  },
};

/** Return a human-readable ARIA label for the given element type. */
function getDefaultLabel(elementType: string): string {
  switch (elementType) {
    case "card":
      return "Card information";
    case "cardNumber":
      return "Card number";
    case "cardExpiry":
      return "Expiration date";
    case "cardCvc":
      return "Security code";
    default:
      return "Card input";
  }
}

/**
 * Map an element type to the corresponding ARIA translation key.
 * Requirements: 11.1, 11.2, 11.3, 11.4
 */
function getAriaKey(elementType: string): TranslationKey {
  switch (elementType) {
    case "cardNumber":
      return "aria.cardNumber";
    case "cardExpiry":
      return "aria.expirationDate";
    case "cardCvc":
      return "aria.cvc";
    case "card":
      return "aria.cardInput";
    default:
      return "aria.cardInput";
  }
}
